import type { ITeamMembers } from "@ports/teamMembers/ITeamMembers";
import type { CreateOperatorInput, TeamMember, UpdateOperatorLoginInput } from "@domain/CRM/teamMember";
import { generateRandomPassword } from "@domain/Auth";

/** Create-operator form data — everything except the password (generated here). */
export type CreateOperatorFormData = Omit<CreateOperatorInput, "password">;

/** Existing-operator form data — everything except the password (generated here). */
export type UpdateOperatorFormData = Omit<UpdateOperatorLoginInput, "password">;

export interface PasswordGeneratorUseCasesDeps {
	teamMembersApi: ITeamMembers;
	/** Pluggable random-password generator. Defaults to a CSPRNG-backed
	 *  alphanumeric generator using `crypto.getRandomValues`. Tests inject
	 *  a deterministic stub. */
	generatePassword?: () => string;
}

/**
 * Drives the Admin → Password Generator page's two symmetrical workflows:
 *  - `updateOperatorAndGeneratePassword(input)` issues a login to an existing operator,
 *    saving the employee fields edited alongside it.
 *  - `createOperatorAndGeneratePassword(input)` onboards a brand-new operator and
 *    generates its login password in one step.
 *  - `listActivePasswordlessMembers()` lists existing operators without a password.
 *
 * Both workflows return the saved record plus the plaintext password so the page can
 * surface the full credential (username + password). Errors propagate; the vm catches
 * and toasts.
 */
export interface PasswordGeneratorUseCases {
	updateOperatorAndGeneratePassword(
		input: UpdateOperatorFormData,
	): Promise<{ teamMember: TeamMember; password: string }>;
	createOperatorAndGeneratePassword(
		input: CreateOperatorFormData,
	): Promise<{ teamMember: TeamMember; password: string }>;
	listActivePasswordlessMembers(): Promise<TeamMember[]>;
}

async function updateOperatorAndGeneratePassword(
	deps: PasswordGeneratorUseCasesDeps,
	input: UpdateOperatorFormData,
): Promise<{ teamMember: TeamMember; password: string }> {
	const password = (deps.generatePassword ?? generateRandomPassword)();
	const teamMember = await deps.teamMembersApi.updateOperatorLogin({ ...input, password });
	return { teamMember, password };
}

async function createOperatorAndGeneratePassword(
	deps: PasswordGeneratorUseCasesDeps,
	input: CreateOperatorFormData,
): Promise<{ teamMember: TeamMember; password: string }> {
	const password = (deps.generatePassword ?? generateRandomPassword)();
	const teamMember = await deps.teamMembersApi.createOperator({ ...input, password });
	return { teamMember, password };
}

export function makePasswordGeneratorUseCases(deps: PasswordGeneratorUseCasesDeps): PasswordGeneratorUseCases {
	return {
		updateOperatorAndGeneratePassword: (input) => updateOperatorAndGeneratePassword(deps, input),
		createOperatorAndGeneratePassword: (input) => createOperatorAndGeneratePassword(deps, input),
		listActivePasswordlessMembers: () => deps.teamMembersApi.listActivePasswordless(),
	};
}
